/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import {UiSelectOption} from 'components/Ui/UiSelectWithGraphics'
import {useTypedSelector} from 'helpers/reducers'
import {driverName} from './driverUtils'
import {Driver} from 'types/entities/Driver'
import {UserType} from 'types/enums/UserType'
import {useCurrentUser} from 'utils/hooks/useCurrentUser'
import SettingsAction from 'store/settings/SettingsAction'
import {useAppDispatch} from 'helpers/store'
import {useCallback, useEffect} from 'react'

export const downloadHandler = (url: string, filename: string) => {
    if (!url) {
        throw new Error('Missing download URL')
    }

    // Let the browser download natively to avoid buffering large CSV payloads in JS memory.
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.rel = 'noopener noreferrer'
    link.style.display = 'none'

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
}

export const getFilenameFromLink = (fileLink: string) => {
    const fullFileName = fileLink.substring(fileLink.lastIndexOf('/') + 1)
    return fullFileName.substring(0, fullFileName.lastIndexOf('.'))
}

// methods
export const useSellers = () => {
    const sellers = useTypedSelector((state) => state.settings.sellers)
    const { user } = useCurrentUser()
    const dispatch = useAppDispatch()
    const sellerOptions: UiSelectOption[] = []

    const otherSellers = sellers
        ? sellers
              .filter((driver: Driver) => driver.user_type === UserType.Seller)
              .filter((driver: Driver) => [22522, 23747, 27032, 33290, 35790, 48791].includes(driver.id))
              .filter((driver) => driver.id !== user?.id)
        : []

    const getSellerOptions: () => UiSelectOption[] = (): UiSelectOption[] => {
        if (!sellers || sellers.length === 0) {
            return []
        }

        if (user) {
            sellerOptions.push({
                label: driverName(user, true),
                value: user.id,
            })
        }

        otherSellers.forEach((driver) => {
            sellerOptions.push({
                label: driverName(driver, true),
                value: driver.id,
            })
        })
        return sellerOptions
    }

    const seller = sellers ? sellers.find((seller) => seller.id == user?.id) : null

    const sellerById = useCallback(
        (user_id: number): Driver =>
            sellers ? sellers.find((seller) => seller.id == user_id) : null,
        [sellers]
    )
    const selectOptions = getSellerOptions()

    const sellersLength = sellers ? sellers.length : 0
    useEffect(() => {
        if (sellersLength === 0) {
            dispatch(SettingsAction.fetchSettings() as any)
        }
    }, [sellersLength, dispatch])

    return {
        seller,
        sellerById,
        selectOptions,
        getSellerOptions,
    }
}
